import { isMap, isSeq, parseDocument, type Document } from "yaml";
import { resolveModules, validateModule } from "./modules";
import { expandTransforms } from "./transforms";
import type { Diagnostic, ModuleDefinition, ModuleRegistry, PatternSpecification } from "./types";
import { validateSpecification } from "./validate";

export { validateSpecification };

/**
 * Parse, resolve modules and validate a pattern specification. Specs that use
 * `modules:` need the registry of module definitions they name; the modules
 * are merged into their host blocks before validation, so the result is a
 * flat specification like any other.
 */
export function parseSpecification(source: string, settings: { modules?: ModuleRegistry } = {}): { specification?: PatternSpecification; diagnostics: Diagnostic[] } {
  try {
    const document = parseDocument(source);
    if (document.errors.length > 0) throw document.errors[0];
    const raw = document.toJS() as unknown;
    applyCommentSources(document, (raw as Partial<PatternSpecification> | null)?.blocks, (blockId) => ["blocks", blockId, "operations"]);
    const resolved = resolveModules(raw, settings.modules);
    if (resolved.diagnostics.some((item) => item.severity === "error")) return { diagnostics: resolved.diagnostics };
    // Transforms become plain transformPoint operations and transformEdge edges.
    const value = expandTransforms(resolved.value as Partial<PatternSpecification>);
    const diagnostics = [...resolved.diagnostics, ...validateSpecification(value)];
    if (diagnostics.some((item) => item.severity === "error")) return { diagnostics };
    return { specification: value as PatternSpecification, diagnostics };
  } catch (error) {
    return { diagnostics: [yamlError(error)] };
  }
}

/** Parse a module file (`module:`, `anchors:`, options and block content). */
export function parseModule(source: string): { module?: ModuleDefinition; diagnostics: Diagnostic[] } {
  try {
    const document = parseDocument(source);
    if (document.errors.length > 0) throw document.errors[0];
    const value = document.toJS() as unknown;
    applyCommentSources(document, value && typeof value === "object" ? { module: value as { operations?: unknown } } : undefined, () => ["operations"]);
    const diagnostics = validateModule(value);
    if (diagnostics.some((item) => item.severity === "error")) return { diagnostics };
    return { module: value as ModuleDefinition, diagnostics };
  } catch (error) {
    return { diagnostics: [yamlError(error)] };
  }
}

function yamlError(error: unknown): Diagnostic {
  return { severity: "error", code: "YAML_SYNTAX", message: error instanceof Error ? error.message : "Invalid YAML." };
}

/**
 * Specs record the book step for a point as a comment above its operation.
 * Copy that comment into `source` when the operation does not set one, so the
 * workspace can show where each point comes from.
 */
function applyCommentSources(document: Document, containers: Record<string, { operations?: unknown } | undefined> | undefined, pathFor: (id: string) => string[]) {
  if (!containers || typeof containers !== "object") return;
  for (const [id, container] of Object.entries(containers)) {
    const node = document.getIn(pathFor(id), true);
    const operations = container?.operations;
    if (!isSeq(node) || !Array.isArray(operations)) continue;
    node.items.forEach((item, index) => {
      const operation = operations[index];
      if (!isMap(item) || !operation || typeof operation !== "object" || operation.source !== undefined) return;
      // The parser attaches a comment above the first item to the list itself.
      const raw = item.commentBefore ?? (index === 0 ? node.commentBefore : undefined);
      const comment = raw?.split("\n").map((line) => line.trim()).filter(Boolean).join(" ");
      if (comment) operation.source = comment;
    });
  }
}
